import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { closeContactDetails, getUser, openContactDetails, triggerMenuOption } from '../stores/contacts-slice-handler';
import ContactMenu from './contact-menu';
import ContactUserPicture from './contact-user-picture';
import { computeUserName, getHumanDeviceOsList } from 'utils/contact-util';
import { ContactMenuOption } from '../../../constants/contacts-constants';
import t from 'utils/translate';
import { selectCompanySettings } from '../../settings/stores/company-settings-slice-handler';
import { sortByProperty } from '../../../utils/functions';

/**
 *
 * @param {string} msisdn
 * @return {JSX.Element}
 * @constructor
 */
export default function ContactDetailsUser({ msisdn }) {
    // make sure that the contact is kept in link with the store
    const user = useSelector(() => getUser(msisdn));

    /**
     * @type {CompanySettings}
     */
    const companySettings = useSelector(selectCompanySettings);

    const menuOptionClick = (contact, feature) => {
        triggerMenuOption(contact, feature);
        closeContactDetails();
    };

    const cssTrickRef = useRef(null);

    if (user === null) {
        return '';
    }

    return (
        <>
            <div className="contact-details-content" ref={cssTrickRef}>
                <div className="contact-user-item as-avatar">
                    <ContactUserPicture
                        onMouseEnter={() => {
                            // very special case when need to resize the user picture, without having the scrollbar over its parent
                            cssTrickRef.current.style.overflow = 'hidden';
                        }}
                        onMouseLeave={() => {
                            setTimeout(() => {
                                cssTrickRef.current.style.overflow = 'auto';
                            }, 100);
                        }}
                        msisdn={user.Msisdn}
                    />
                </div>
                <div className={'contact-name-container'}>{computeUserName(user)}</div>
                <ContactMenu
                    contact={user}
                    linear
                    optionClick={menuOptionClick}
                    ignoredOptions={[
                        // details are open, no need this button
                        ContactMenuOption.DETAILS,
                        // these trigger CUG update, and we do not want that while details are open
                        ContactMenuOption.ADD_TO_FAVORITES,
                        ContactMenuOption.REMOVE_FROM_FAVORITES,
                        ContactMenuOption.ADD_TO_CONTACTS,
                        ContactMenuOption.REMOVE_FROM_CONTACTS,
                    ]}
                />
                <div className="details-table">
                    <table>
                        <tbody>
                            {user.UserCustomStatus.length > 0 && (
                                <tr>
                                    <td>{t('js_all_status')}:</td>
                                    <td>{user.UserCustomStatus}</td>
                                </tr>
                            )}
                            <tr>
                                <td>{t('js_all_contacts_details_phone_number')}:</td>
                                <td data-test-id="contact-details-user-msisdn">{user.Msisdn}</td>
                            </tr>
                            {user.Email.length > 0 && (
                                <tr>
                                    <td>{t('js_all_contacts_details_email_address')}:</td>
                                    <td data-test-id="contact-details-user-email">
                                        <a href={`mailto:${user.Email}`}>{user.Email}</a>
                                    </td>
                                </tr>
                            )}
                            {user.Position.length > 0 && (
                                <tr>
                                    <td>{t('js_all_contacts_details_position')}:</td>
                                    <td data-test-id="contact-details-user-position">{user.Position}</td>
                                </tr>
                            )}
                            {companySettings.AllowWalkieTalkie &&
                                companySettings.AllowPriority &&
                                getHumanDeviceOsList().includes(user.DeviceOs) && (
                                    <tr>
                                        <td>{t('js_all_contacts_details_priority_level')}:</td>
                                        <td data-test-id="contact-details-user-priority">
                                            <div className="priority-display">{user.Priority}</div>
                                        </td>
                                    </tr>
                                )}
                        </tbody>
                    </table>
                </div>
                {user.Groups.length > 0 && (
                    <div className="user-groups-container">
                        <div className={'title-label'}>{t('js_all_contacts_details_groups')}:</div>
                        <div className="groups-list">
                            {sortByProperty([...user.Groups], 'Name').map(
                                /**
                                 * @param {CugGroup} group
                                 */
                                (group) => (
                                    <div
                                        className="group-name"
                                        key={`user-details-group-${group.GroupId}`}
                                        data-test-id={`user-details-group-${group.GroupId}`}
                                        onClick={() => openContactDetails(group.GroupId, true)}
                                    >
                                        <span className="stw-group"></span>
                                        {group.Name}
                                    </div>
                                ),
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className="footer-bar">
                <div className="ok-button" data-test-id="user-details-ok-button" onClick={() => closeContactDetails()}>
                    {t('js_all_ok_button')}
                </div>
            </div>
        </>
    );
}
